import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import { CustomMention } from "./CustomMention";
import Toolbar from "./toolbar";
import { useTranslation } from "react-i18next";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { mentionSuggestion } from "./mention-suggestion";

export interface TiptapRef {
  editor: Editor | null;
}

interface TiptapProps {
  content: string;
  onChange: (text: string) => void;
  placeholder?: string;
  enableMention?: boolean;
  enableLink?: boolean;
  enableAutolink?: boolean;
  /** Shows the emoji picker in the editor toolbar. Leave off where the surrounding
   *  form already provides its own emoji button (e.g. the community composer). */
  enableEmoji?: boolean;
  disabled?: boolean;
}

const Tiptap = forwardRef<TiptapRef, TiptapProps>(
  (
    {
      content,
      onChange,
      placeholder,
      enableMention = false,
      enableLink = false,
      enableAutolink = true,
      enableEmoji = false,
      disabled = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // Memoize extensions to update when enableLink or enableMention changes
    const extensions = useMemo(() => {
      const exts = [
        StarterKit.configure(),
        Underline,
        Placeholder.configure({
          placeholder: placeholder || t("post_job_page.job_description_required"),
        }),
        BulletList.configure({
          keepMarks: true,
          keepAttributes: true,
          HTMLAttributes: {
            class: "px-6 my-3 list-disc",
          },
        }),
        OrderedList.configure({
          keepMarks: true,
          keepAttributes: true,
          HTMLAttributes: {
            class: "px-6 my-3 list-decimal",
          },
        }),
        // Always include Link extension so links are visible in the editor
        Link.configure({
          openOnClick: false,
          autolink: enableAutolink,
          linkOnPaste: true,
          validate: (href) => /^(https?:\/\/|www\.)/.test(href),
          HTMLAttributes: {
            class: "text-primary underline underline-offset-2 cursor-pointer",
          },
        }).extend({
          // Auto-prepend https:// to www. links
          addOptions() {
            return {
              ...this.parent?.(),
            };
          },
          addAttributes() {
            return {
              ...this.parent?.(),
              href: {
                default: null,
                parseHTML: (element) => {
                  const href = element.getAttribute("href");
                  if (href && href.startsWith("www.")) {
                    return `https://${href}`;
                  }
                  return href;
                },
              },
            };
          },
        }),
      ];

      if (enableMention) {
        exts.push(
          CustomMention.configure({
            HTMLAttributes: {
              class: "mention text-primary font-semibold",
            },
            suggestion: mentionSuggestion,
          }) as any,
        );
      }

      return exts;
    }, [enableMention, enableAutolink, placeholder, t]);

    const editor = useEditor(
      {
        extensions,
        editable: !disabled,
        editorProps: {
          attributes: {
            class:
              "flex min-h-24 w-full flex-col items-start justify-start px-3 py-2.5 text-sm leading-7 font-normal text-foreground outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
            placeholder: "Enter your job description",
          },
          handleKeyDown: (view, event) => {
            // When space is pressed inside a link, exit the link formatting
            if (event.key === " ") {
              const { state } = view;
              const { selection } = state;
              const { $from } = selection;

              // Check if we're inside a link mark
              const linkMark = $from.marks().find((mark) => mark.type.name === "link");
              if (linkMark) {
                // Insert space and remove link mark from cursor position
                const tr = state.tr.insertText(" ");
                tr.removeStoredMark(linkMark.type);
                view.dispatch(tr);
                return true; // Prevent default behavior
              }
            }
            return false;
          },
        },
        onUpdate: ({ editor }) => {
          onChange(editor.getHTML());
        },
        content,
        parseOptions: {
          preserveWhitespace: "full",
        },
      },
      [extensions, disabled],
    );

    useImperativeHandle(ref, () => ({
      editor,
    }));

    return (
      <div className="focus-within:ring-ring focus-within:ring-offset-background border-input bg-background w-full rounded-lg border transition-shadow focus-within:ring-2 focus-within:ring-offset-2">
        <EditorContent className="whitespace-pre-line" editor={editor} />
        <Toolbar editor={editor} enableLink={enableLink} enableEmoji={enableEmoji} />
      </div>
    );
  },
);

Tiptap.displayName = "Tiptap";

export default Tiptap;
